import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

import { DataService } from "base/services";
import { FIELD_TYPES } from "base/constants";
import { FormDataWrapper } from "base/form";
import { useUserStore } from "base/stores";

import { hasFeatures } from "../components/primitives/featuregating/index.jsx";

export const NUMBER_FIELDS = [FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY];

const TYPE_MAP = {
  [FIELD_TYPES.TEXT]: ["string", "phoneNumber"],
  [FIELD_TYPES.NUMBER]: ["number", "float"],
  [FIELD_TYPES.CURRENCY]: ["number", "float"],
  [FIELD_TYPES.SELECT]: ["string"],
  [FIELD_TYPES.REFERENCE]: ["string"],
  [FIELD_TYPES.DATE]: ["date"],
  [FIELD_TYPES.DATE_TIME]: ["date"]
};
const FEATURE_ALLOW_SMART_ATTACHMENT_FIELD =
  "process_allow_smartattachment_field";
const SUPPORTED_FIELD_TYPES = Object.keys(TYPE_MAP);

export const SMART_ATTACHMENT_WIDGET = {
  FILE_TYPES: ["JPG", "JPEG", "PNG", "PDF"],
  /**
   * 20 MB
   */
  MAX_SIZE: 1024 * 1024 * 20
};

export function hasSmartAttachmentFeature() {
  return hasFeatures([FEATURE_ALLOW_SMART_ATTACHMENT_FIELD]);
}
export function canUseSuggestions(field) {
  return (
    field &&
    hasSmartAttachmentFeature() &&
    SUPPORTED_FIELD_TYPES.includes(field.getValue("Type"))
  );
}
export async function getLookupValue({
  accountId,
  flowType,
  flowId,
  instanceId,
  activityInstanceId,
  fieldId,
  query,
  tableId,
  tableRowId
}) {
  function getLookup({ baseUrl, fieldId, tableId, tableRowId }) {
    if (tableId && tableRowId) {
      return `${baseUrl}/${tableId}/${tableRowId}/${fieldId}/lookup`;
    } else {
      return `${baseUrl}/${fieldId}/lookup`;
    }
  }
  const baseUrl = `/${flowType}/2/${accountId}/${flowId}/${instanceId}/${activityInstanceId}`;
  const searchUrl = getLookup({
    baseUrl,
    fieldId,
    tableId,
    tableRowId
  });
  const [getLookupData] = DataService.get(searchUrl, {
    toast: false,
    query: { q: query, page_number: 1, page_size: 10 }
  });
  try {
    return await getLookupData();
  } catch (e) {
    console.error(e);
    return { Data: [] };
  }
}

export async function loadSuggestions({
  accountId,
  flowType,
  flowId,
  instanceId,
  activityInstanceId
}) {
  const [getSuggestions] = DataService.get(
    `/${flowType}/2/${accountId}/${flowId}/${instanceId}/${activityInstanceId}/documentparser/suggestions`,
    {
      toast: false
    }
  );
  try {
    return await getSuggestions();
  } catch (e) {
    console.error(e);
    return [];
  }
}
dayjs.extend(timezone);

function canSaveChanges(targetField) {
  return targetField.getValue("Type") === FIELD_TYPES.REFERENCE;
}
export function resolveCurrencyType(currencyTypes = ["USD"], meta = {}) {
  const currencyType =
    (meta.currency &&
      currencyTypes.find(
        (type) => type.toUpperCase() === meta.currency.value.toUpperCase()
      )) ||
    currencyTypes.find((type) => type === "USD") ||
    currencyTypes[0];
  return currencyType;
}
async function formatSuggestionValue(
  useFormStore,
  suggestion,
  targetField,
  flowData,
  tableId,
  tableRowId
) {
  const { value: rawValue, meta } = suggestion;
  if (targetField.getValue("Type") === FIELD_TYPES.REFERENCE) {
    await useFormStore.getState().saveChanges(useFormStore.getState().formData);
    const result = await getLookupValue({
      ...flowData,
      fieldId: targetField.getValue("Id"),
      query: rawValue,
      tableId,
      tableRowId
    });
    return result.Data[0];
  }
  if (targetField.getValue("Type") === FIELD_TYPES.CURRENCY) {
    const currencyType = resolveCurrencyType(
      targetField.getValue("CurrencyTypes"),
      meta
    );
    return `${rawValue} ${currencyType}`;
  }
  if (targetField.getValue("Type") === FIELD_TYPES.DATE_TIME) {
    const { currentUser } = useUserStore.getState();
    const userTimeZone =
      (currentUser && currentUser.TimeZone._id) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeStr = `${dayjs
      .tz(rawValue, userTimeZone)
      .format("YYYY-MM-DDTHH:mm:00Z")} ${userTimeZone}`;
    return timeStr;
  } else {
    return rawValue;
  }
}

export function getSuggestionList({
  results = [],
  fieldId,
  rowIndex,
  tableId
}) {
  function mergeResults(results) {
    const max = Math.max(...results.map((l) => l.length));
    const result = new Set();
    const map = {};
    for (let i = 0; i < max; i++) {
      for (const list of results) {
        const suggestion = list[i];
        if (suggestion?.value) {
          result.add(suggestion.value);
          map[suggestion.value] = suggestion.meta;
        }
      }
    }
    return Array.from(result).map((value) => ({ value, meta: map[value] }));
  }

  function getSuggestionListForField({
    fieldId,
    rowIndex,
    tableId,
    result = {}
  }) {
    const { tableSuggestions, formSuggestions } = result;
    if (tableId) {
      if (tableSuggestions && tableSuggestions[tableId]?.length > rowIndex) {
        return tableSuggestions[tableId][rowIndex][fieldId]?.results || [];
      }
    } else if (formSuggestions) {
      return formSuggestions[fieldId]?.results || [];
    }
    return [];
  }
  if (fieldId && results.length) {
    results = results.map((result) =>
      getSuggestionListForField({ fieldId, rowIndex, tableId, result })
    );
    return mergeResults(results);
  }
  return [];
}
function processResults(results = []) {
  function trim(entry) {
    if (typeof entry.value === "string" || entry.value instanceof String) {
      if (entry.type === "float") {
        entry.value = +entry.value?.replace(/[^\d.]/g, "");
      } else entry.value = entry.value?.replace(/^\n|\n$/g, "");
    }

    return entry;
  }
  return results.map((entry) => trim(entry));
}

function getFieldMapByLabel(fields = []) {
  return fields
    .map((item) => ({
      ...item,
      label: item.label.toLowerCase().replaceAll("_", "")
    }))
    .reduce((map, field) => ({ ...map, [field.label]: field }), {});
}
function getFilteredList(documentFields = {}, similarDocumentLabels) {
  if (!similarDocumentLabels) return [];
  return processResults(
    similarDocumentLabels
      .map((l) => l.split(" ").join(""))
      .filter((l) => documentFields[l])
      .map((l) => documentFields[l])
  );
}
/**
 * list suggestions for given `field` in `documentFields` list based on similarity sorting
 * result in `similarityMap`
 * @param {*} field
 * @param {*} documentFields
 * @param {*} similarityMap
 * @returns
 */
function getSuggestionsForField(
  field = {},
  documentFields = {},
  similarityMap = {},
  meta = {}
) {
  const { name, dataType } = field;
  if (TYPE_MAP[dataType]) {
    let similarTypes = TYPE_MAP[dataType] || [];
    let suggestionList = getFilteredList(
      documentFields,
      similarityMap[name?.toLowerCase()]
    ).filter((item) => similarTypes.includes(item.type));
    return suggestionList.map((item) => ({ value: item.value, meta }));
  }
  return [];
}
function getMetaDataFromDocumentResults(documentMeta = []) {
  return documentMeta.reduce(
    (meta, item) => ({ ...meta, [item.label]: item }),
    {}
  );
}
/**
 * Parse given document parsed result from api and create field <=> suggestion map
 * @param {*} model
 * @param {*} data
 * @returns
 */
export function parseDocumentResults(useFormStore, data) {
  const {
    File: attachment,
    AnalysisResult: {
      items: documentItems,
      fields: documentFields,
      others: documentMeta
    },
    SortedLabelResults: [fieldsSimMap, tableSimMap]
  } = data;
  const { schema: model } = useFormStore.getState();
  const formFields = model.getList("Model", "Field");
  const documentFieldsMap = getFieldMapByLabel(documentFields);
  const meta = getMetaDataFromDocumentResults(documentMeta);

  const formSuggestions = formFields
    .map((formField) => {
      const { Id: id, Name: name, Type: dataType } = formField.toJSON();
      return {
        formField,
        results: getSuggestionsForField(
          { id, name, dataType },
          documentFieldsMap,
          fieldsSimMap,
          meta
        )
      };
    })
    .reduce((map, entry) => ({ ...map, [entry.formField.id]: entry }), {});

  const tableSuggestions = model
    .getList("Model", "Model")
    .map((childModel) => {
      const tableFields = childModel.getList("Model", "Field");
      const mappedTableSuggestions = documentItems.map((itemRow) => {
        const fieldsMap = getFieldMapByLabel(itemRow);
        return tableFields
          .map((formField) => {
            const { Id: id, Name: name, Type: dataType } = formField.toJSON();
            return {
              formField,
              results: getSuggestionsForField(
                { id, name, dataType },
                fieldsMap,
                tableSimMap,
                meta
              )
            };
          })
          .reduce(
            (map, entry) => ({ ...map, [entry.formField.id]: entry }),
            {}
          );
      });
      return { tableId: childModel.id, suggestions: mappedTableSuggestions };
    })
    .reduce(
      (map, entry) => ({ ...map, [entry.tableId]: entry.suggestions }),
      {}
    );
  return {
    formSuggestions,
    tableSuggestions,
    attachment
  };
}
function canUpdateField(fieldId, config) {
  if (config) {
    const fieldConfig = Object.values(config).find(
      (field) => field.id === fieldId
    );
    return fieldConfig && !fieldConfig.isHidden && !fieldConfig.isReadOnly;
  }
  return false;
}
function canUpdateTableField(fieldId, config) {
  if (config) {
    const fieldConfig = config.find((field) => field.Id === fieldId);
    return (
      fieldConfig &&
      !fieldConfig.isHidden &&
      !fieldConfig.isReadOnly &&
      !fieldConfig.ReadOnly
    );
  }
  return false;
}
/**
 * Auto fill the form with given suggestion data
 * fill supported form fields and table
 * @param {*} useFormStore
 * @param {*} modelConfig
 * @param {*} model
 * @param {*} formData
 * @param {*} param4
 */
export async function updateFormWithSuggestions(
  useFormStore,
  { formSuggestions, tableSuggestions },
  flowData
) {
  const { modelConfig, schema: model, formData } = useFormStore.getState();
  for (const [fieldId, entry] of Object.entries(formSuggestions)) {
    if (
      formData.getValue(fieldId) === "" &&
      entry.results.length &&
      canUpdateField(fieldId, modelConfig[model.getValue("Id")])
    ) {
      const formattedValue = await formatSuggestionValue(
        useFormStore,
        entry.results[0],
        entry.formField,
        flowData
      );
      if (formattedValue !== undefined)
        useFormStore
          .getState()
          .updateField(
            formattedValue,
            entry.formField.toJSON(),
            canSaveChanges(entry.formField)
          );
    }
  }

  model.getList("Model", "Model").forEach((childModel) => {
    const currentTableSuggestions = tableSuggestions[childModel.id];
    if (
      currentTableSuggestions &&
      canUpdateField(childModel.id, modelConfig[model.getValue("Id")])
    ) {
      const validCellsInFirstRow = Object.values(
        currentTableSuggestions[0] || []
      ).find((entry) => entry.results.length);
      if (
        formData.getList(childModel.id).length === 0 &&
        validCellsInFirstRow
      ) {
        for (const [index, row] of currentTableSuggestions.entries()) {
          const validCells = Object.values(row).filter(
            (entry) => entry.results.length
          );
          useFormStore
            .getState()
            .addTableRow({ tableId: childModel.id })
            .then(async (_) => {
              for (const [cellIndex, entry] of validCells.entries()) {
                try {
                  if (
                    entry.results.length &&
                    canUpdateTableField(
                      entry.formField.id,
                      modelConfig[childModel.id]
                    )
                  ) {
                    const tableRowId = useFormStore
                      .getState()
                      .formData.getList(childModel.id)[index]?.id;
                    const formattedValue = await formatSuggestionValue(
                      useFormStore,
                      entry.results[0],
                      entry.formField,
                      flowData,
                      childModel.id,
                      tableRowId
                    );
                    if (formattedValue !== undefined) {
                      useFormStore
                        .getState()
                        .updateTableField(
                          childModel.id,
                          tableRowId,
                          { modelConfig, ...entry.formField.toJSON() },
                          formattedValue,
                          cellIndex === validCells.length - 1 &&
                            index === currentTableSuggestions.length - 1
                        );
                    }
                  }
                } catch (e) {
                  console.error(e);
                }
              }
            });
        }
      }
    }
  });

  useFormStore.getState().mergeChanges();
  useFormStore.setState({
    formData: FormDataWrapper(formData.dataSet, formData.changeSet)
  });
}
